import type { TerrainChange } from '../network/gameStateSync';
import { CollisionMask } from '../sim/collisionMask';

export { CollisionMask };

/**
 * Browser adapter over the pure CollisionMask: collision state lives in the
 * Uint8Array mask (shared with the headless simulation), while destruction is
 * mirrored onto the visual terrain canvas so what you see matches collision.
 */
export class TerrainCollisionMask extends CollisionMask {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;

  /**
   * @param canvas visual terrain canvas (mirrored for crater rendering)
   * @param initialAlpha authoritative collision alpha (e.g. from MaskCanvas
   *   generation). When omitted, the alpha channel is imported from the
   *   canvas (legacy path: tests, save/load).
   */
  constructor(canvas: HTMLCanvasElement, initialAlpha?: Uint8Array) {
    super(canvas.width, canvas.height);
    this.canvas = canvas;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) throw new Error('Failed to get 2D context');
    this.ctx = ctx;
    if (initialAlpha) {
      this.setAlphaData(initialAlpha);
    } else {
      this.importAlphaFromCanvas();
    }
  }

  /** Re-import the alpha channel from the canvas into the collision mask. */
  refreshFullImageData(): void {
    this.importAlphaFromCanvas();
  }

  private importAlphaFromCanvas(): void {
    const width = this.getWidth();
    const height = this.getHeight();
    const imageData = this.ctx.getImageData(0, 0, width, height);
    const src = imageData.data;
    const alpha = new Uint8Array(width * height);
    for (let i = 0; i < alpha.length; i++) {
      alpha[i] = src[i * 4 + 3];
    }
    this.setAlphaData(alpha);
  }

  override destroyCircle(
    cx: number,
    cy: number,
    radius: number,
    type: TerrainChange['type'] = 'explosion',
  ): void {
    super.destroyCircle(cx, cy, radius, type);
    this.eraseCircleVisual(cx, cy, radius);
  }

  override applyModifications(mods: TerrainChange[]): void {
    super.applyModifications(mods);
    for (const mod of mods) {
      if (mod.type === 'girder') continue; // rasterized by the caller
      this.eraseCircleVisual(mod.x, mod.y, mod.radius);
    }
  }

  private eraseCircleVisual(cx: number, cy: number, radius: number): void {
    this.ctx.globalCompositeOperation = 'destination-out';
    this.ctx.beginPath();
    this.ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    this.ctx.fill();
    this.ctx.globalCompositeOperation = 'source-over';
  }

  getCanvas(): HTMLCanvasElement {
    return this.canvas;
  }
}
